import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from "@mui/material";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";
import ToastManager from "../../Components/ToastManager";
import { NetworkManager } from "../../Components/NetworkManager";
import { CommonUtil, readJson } from "../../utils/CommonUtil";

const validationSchema = Yup.object().shape({});

export interface CustomAudience {
  customAudienceName: string;
  customAudienceDesc: string;
}

interface CreateCustomAudienceDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (audience: CustomAudience) => void;
  fetchCustomAudiences: () => void;
}

interface AudienceFormValues {
  customAudienceName: string;
  customAudienceDesc: string;
}

export const CreateCustomAudienceDialog: React.FC<
  CreateCustomAudienceDialogProps
> = ({ open, onClose, onSave, fetchCustomAudiences }) => {
  const initialValues: AudienceFormValues = {
    customAudienceName: "",
    customAudienceDesc: "",
  };

  const {
    facebook: { pageId },
  } = readJson("fbbPageDetail") || "{}";
  const { adAccountId } = readJson("fbbAdAccountDetail") || "{}";

  const handleSubmit = async (
    values: AudienceFormValues,
    formikHelpers: FormikHelpers<AudienceFormValues>,
    onSave: (customAudience: CustomAudience) => void,
    onClose: () => void,
  ) => {
    const payload = {
      pageId,
      adAccountId,
      customAudienceName: values.customAudienceName,
      customAudienceDesc: values.customAudienceDesc,
    };

    try {
      NetworkManager.sendJsonRequest(
        "broadcastCustomAudiences/createCustomAudience",
        payload,
        "",
        "POST",
        true,
        (data: any) => {
          const respData = CommonUtil.parseData(data);
          if (respData?.statusCode === "BM0000") {
            const createdCustomAudience: CustomAudience = {
              customAudienceName: values.customAudienceName,
              customAudienceDesc: values.customAudienceDesc,
            };
            onSave(createdCustomAudience);
            ToastManager.showToast(
              "New Custom Audience created successfully.",
              "Success",
            );
            formikHelpers.resetForm();
            onClose();
            fetchCustomAudiences();
          } else if (respData?.statusCode === "BM0001") {
            ToastManager.showToast(
              "Name is already taken. Please choose another one.",
              "Error",
            );
          }
        },
        () => {
          ToastManager.showToast(
            "Error while creating custom audience.",
            "Error",
          );
        },
      );
    } catch (error) {
      ToastManager.showToast("Unexpected error occurred.", "Error");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          onClose();
        }
      }}
      fullWidth
      slotProps={{
        paper: { sx: { maxHeight: "100%", maxWidth: "40%" } },
      }}>
      <DialogTitle sx={{ textAlign: "center" }}>Create Custom Audience</DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, helpers) =>
          handleSubmit(values, helpers, onSave, onClose)
        }>
        {({ handleChange, values, errors, touched }) => (
          <Form>
            <DialogContent
              sx={{
                pt: 2,
                maxHeight: "400px",
                overflowY: "auto",
              }}>
              <Box>
                <TextField
                  fullWidth
                  required
                  name="customAudienceName"
                  label="Custom Audience Name"
                  value={values.customAudienceName}
                  onChange={handleChange}
                  error={
                    touched.customAudienceName &&
                    Boolean(errors.customAudienceName)
                  }
                  helperText={
                    touched.customAudienceName && errors.customAudienceName
                  }
                  sx={{ mt: 1 }}
                />

                <TextField
                  fullWidth
                  required
                  multiline
                  minRows={6}
                  name="customAudienceDesc"
                  label="Description"
                  placeholder="Brief description of who is included in this audience"
                  value={values.customAudienceDesc}
                  onChange={handleChange}
                  error={
                    touched.customAudienceDesc &&
                    Boolean(errors.customAudienceDesc)
                  }
                  helperText={
                    touched.customAudienceDesc && errors.customAudienceDesc
                  }
                  sx={{ mt: 2 }}
                />
              </Box>
            </DialogContent>

            <DialogActions sx={{ justifyContent: "space-between", p: 3 }}>
              <Button onClick={onClose} color="primary">
                Cancel
              </Button>

              <Button type="submit" variant="contained" color="primary">
                Create Audience
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
